
import { Link } from "react-router-dom";
import { Plus, Pencil } from "lucide-react";
import { useAdminPeople } from "@/hooks/use-admin-queries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CATEGORY_LABELS } from "@/lib/types";
import type { Category } from "@/lib/types";

export default function PeopleListPage() {
  const { data, isLoading, error } = useAdminPeople();

  if (isLoading) {
    return <p className="text-muted-foreground">Loading people…</p>;
  }

  if (error || !data) {
    return <p className="text-red-600">Could not load people.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">People</h2>
          <p className="text-sm text-muted-foreground">
            Manage devotees, employees, and device PINs
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/people/new">
            <Plus className="mr-1 h-4 w-4" />
            Add person
          </Link>
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((person) => (
          <Card key={person.id}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{person.name}</CardTitle>
                <Button variant="ghost" size="icon" asChild>
                  <Link to={`/admin/people/${person.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">{person.phone}</p>
              <div className="flex flex-wrap gap-1">
                {person.categories.map((c: Category) => (
                  <Badge key={c} variant="secondary">
                    {CATEGORY_LABELS[c]}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Type: {person.person_type}
                {!person.active && " · Inactive"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.length === 0 && (
        <p className="text-muted-foreground">No people yet. Add your first person.</p>
      )}
    </div>
  );
}
